import { addDoc, collection, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { queueNotifications } from './notifications';

/** Same values as the iOS `CheckInType` and the Android check-in model. */
export type CheckInType = 'morning' | 'night';

/**
 * English greeting stored in Firestore and pushed to the circle — canonical data
 * read by both apps, so never localised (matches iOS `CheckInType.greeting`).
 */
const GREETINGS: Record<CheckInType, string> = {
  morning: "Good morning! I'm up and having breakfast",
  night: 'Good night! Going to bed now',
};

export interface CheckInRequest {
  userId: string;
  userName: string;
  businessId: string;
  type: CheckInType;
  /** User ids of the circle members to notify. The sender is skipped automatically. */
  recipientIds: string[];
}

/**
 * Record a Good Morning / Good Night check-in exactly as the iOS app does: one
 * `check_ins` document (iOS `CheckIn` model fields), then a check-in notification
 * queued for each recipient.
 */
export async function sendCheckIn(request: CheckInRequest): Promise<void> {
  const { userId, userName, businessId, type } = request;
  const recipientIds = [...new Set(request.recipientIds)].filter((id) => id && id !== userId);
  const now = Timestamp.now();
  const message = GREETINGS[type];

  await addDoc(collection(db, 'check_ins'), {
    userId,
    userName,
    businessId,
    type,
    message,
    notifyRecipients: recipientIds,
    timestamp: now,
    createdAt: now,
  });

  await queueNotifications(
    recipientIds.map((recipientId) => ({
      recipientId,
      senderId: userId,
      businessId,
      type: 'check_in',
      title: `${userName} Checked In`,
      body: `${userName} says ${message}`,
      data: { checkInType: type, userName, userId },
    }))
  );
}
